import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect, type SelectOption } from 'components/ui';
import { ConfirmDialog } from 'components/modals';
import DocumentNumberField from 'components/common/DocumentNumberField';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAuthContext } from 'contexts/AuthContext';
import { journalEntryService, voucherTypeService, coaLedgerService, entityAccountingConfigService } from 'api/services';
import type { VoucherTypeDto, CreateJournalEntryRequest, TransactionDto } from 'api/types';
import { FyDateInput } from 'components/common/FyDateInput';
import { useSelectedFinancialYear, defaultDateIn } from 'hooks/useSelectedFinancialYear';

// ============================================================================
// JOURNAL ENTRY FORM MODAL — posts a manual, balanced voucher (ADR-0015).
// Create-only: posted vouchers are immutable and corrected by reversal.
// ============================================================================

const JOURNAL_CLASSES = ['JOURNAL', 'CONTRA'];

const ALLOC_MODE_OPTIONS: SelectOption[] = [
  { value: 'NEW_REF', label: 'New Ref (new bill)' },
  { value: 'AGAINST_REF', label: 'Against Ref (settle bill)' },
  { value: 'ADVANCE', label: 'Advance' },
  { value: 'ON_ACCOUNT', label: 'On Account' },
];

interface AllocRow {
  key: number;
  mode: string;
  referenceNo: string;
  openItemId: string;
  openItemLabel: string;
  dueDate: string;
  amount: string;
}

interface LineRow {
  key: number;
  ledgerId: string;
  ledgerLabel: string;
  ledgerIsOpenItem: boolean;
  narration: string;
  debit: string;
  credit: string;
  allocations: AllocRow[];
  /**
   * Open-item line with no bill-wise rows: how the whole amount is kept on the party's ledger —
   * ON_ACCOUNT (default; matched later by set-off or Against Ref), ADVANCE, or NEW_REF (a bill).
   */
  keepAs?: string;
}

/** What an unallocated open-item line becomes (the server defaults to On Account too). */
const KEEP_AS_OPTIONS: SelectOption[] = [
  { value: 'ON_ACCOUNT', label: 'On Account' },
  { value: 'ADVANCE', label: 'Advance' },
  { value: 'NEW_REF', label: 'New Ref (new bill)' },
];

interface JournalForm {
  voucherTypeId: string;
  date: string;
  reference: string;
  narration: string;
  manualNumber: string;
}

const today = () => new Date().toISOString().slice(0, 10);
const num = (s: string) => { const n = parseFloat(s); return isNaN(n) ? 0 : n; };
const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export interface JournalEntryFormModalProps {
  isOpen: boolean;
  /**
   * 'create' (default) posts a new voucher.
   * 'edit' alters an existing unlocked one.
   * 'copy' seeds the form from an existing voucher and posts a NEW independent voucher.
   */
  mode?: 'create' | 'edit' | 'copy';
  /** Edit: the voucher being edited. Copy: the source voucher to seed the new draft from. */
  transaction?: TransactionDto | null;
  onPosted: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  createInstanceId?: string;
  copyInstanceId?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, transactionNo?: string) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

export const JournalEntryFormModal: React.FC<JournalEntryFormModalProps> = ({
  isOpen, mode = 'create', transaction, onPosted, onClose, tabId, moduleName, createInstanceId, copyInstanceId, onRestoreModal, minimizeRef,
}) => {
  const isEdit = mode === 'edit';
  const isCopy = mode === 'copy';
  const { minimize } = useModalMinimize();
  const { selectedBusinessUnit } = useAuthContext();
  const sessionBuId = selectedBusinessUnit?.id;

  const lineKey = useRef(0);
  const allocKey = useRef(0);
  const blankLine = useCallback((): LineRow => ({ key: ++lineKey.current, ledgerId: '', ledgerLabel: '', ledgerIsOpenItem: false, narration: '', debit: '', credit: '', allocations: [] }), []);
  const blankAlloc = useCallback((): AllocRow => ({ key: ++allocKey.current, mode: 'NEW_REF', referenceNo: '', openItemId: '', openItemLabel: '', dueDate: '', amount: '' }), []);

  const fy = useSelectedFinancialYear();
  const fyRef = useRef(fy); fyRef.current = fy;
  const initialForm = useCallback((): JournalForm => ({ voucherTypeId: '', date: defaultDateIn(fyRef.current), reference: '', narration: '', manualNumber: '' }), []);

  const [form, setForm] = useState<JournalForm>(initialForm);
  const [lines, setLines] = useState<LineRow[]>(() => [blankLine(), blankLine()]);
  const [voucherTypes, setVoucherTypes] = useState<VoucherTypeDto[]>([]);
  const [functionalCurrency, setFunctionalCurrency] = useState('INR');
  // As-of-voucher-date ledger balances (signed, Dr positive), keyed by ledger id (ADR-0017).
  const [ledgerBalances, setLedgerBalances] = useState<Record<string, number>>({});

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [posting, setPosting] = useState(false);
  const [conflict, setConflict] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [refreshing, setRefreshing] = useState(false);

  const formRef = useRef(form); formRef.current = form;
  const linesRef = useRef(lines); linesRef.current = lines;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId); copyInstanceIdRef.current = copyInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  const modeRef = useRef(mode); modeRef.current = mode;
  const transactionRef = useRef(transaction); transactionRef.current = transaction;
  const editIdRef = useRef<string | null>(null);
  const rowVersionRef = useRef(0);

  // Snapshot for dirty tracking — includes ONLY user-editable fields. Derived
  // cache fields (ledgerLabel, ledgerIsOpenItem, key) can change asynchronously
  // when SyutSelect callbacks populate their local caches; if we included them,
  // the "any unsaved changes?" prompt would trigger on close even after a
  // no-op open. Keep this list aligned with the actual form controls.
  const snapshot = useCallback((f: JournalForm, l: LineRow[]) => JSON.stringify({
    f,
    l: l.map(x => ({
      ledgerId: x.ledgerId,
      narration: x.narration || '',
      debit: x.debit || '',
      credit: x.credit || '',
      keepAs: x.keepAs || '',
      allocations: (x.allocations || []).map(a => ({
        mode: a.mode || '',
        referenceNo: a.referenceNo || '',
        openItemId: a.openItemId || '',
        dueDate: a.dueDate || '',
        amount: a.amount || '',
      })),
    })),
  }), []);
  const ledgerLabelCache = useRef<Map<string, string>>(new Map());
  const ledgerOpenItemCache = useRef<Map<string, boolean>>(new Map());
  const billLabelCache = useRef<Map<string, string>>(new Map());

  // Reset / restore on open. Uses a cancel-token so React 18 StrictMode's
  // double-invoke doesn't leave two competing async loads writing to state +
  // snapshot — the second callback would race and re-baseline against a
  // slightly different lines array (different keys / cached is-open-item
  // values), causing "unmodified restore → dirty" false positives.
  // Aligns with the template's useMinimizableForm pattern.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }
    const cancelToken = { cancelled: false };
    if (modeRef.current === 'edit' && transactionRef.current) {
      editIdRef.current = transactionRef.current.id;
      rowVersionRef.current = transactionRef.current.rowVersion;
      journalEntryService.getById(transactionRef.current.id).then(tx => {
        if (cancelToken.cancelled) return;
        const f: JournalForm = {
          voucherTypeId: tx.voucherTypeId,
          date: (tx.transactionDate || '').slice(0, 10) || today(),
          reference: tx.reference || '',
          narration: tx.narration || '',
          manualNumber: tx.transactionNo,
        };
        const mapped: LineRow[] = (tx.lines || []).map(ln => ({
          key: ++lineKey.current,
          ledgerId: ln.ledgerId,
          ledgerLabel: ln.ledgerCode ? `${ln.ledgerCode} - ${ln.ledgerName}` : (ln.ledgerName || ''),
          ledgerIsOpenItem: ledgerOpenItemCache.current.get(ln.ledgerId) ?? false,
          narration: ln.narration || '',
          debit: ln.drAmount ? String(ln.drAmount) : '',
          credit: ln.crAmount ? String(ln.crAmount) : '',
          allocations: [],
        }));
        const l = mapped.length >= 2 ? mapped : [...mapped, blankLine(), blankLine()];
        rowVersionRef.current = tx.rowVersion;
        setForm(f);
        setLines(l);
        initialSnapshotRef.current = snapshot(f, l);
      }).catch((err: any) => {
        if (cancelToken.cancelled) return;
        notificationService.toast.error(err?.response?.data?.message || 'Could not load the voucher.');
      });
      return () => { cancelToken.cancelled = true; };
    } else if (modeRef.current === 'copy' && transactionRef.current) {
      // Copy: seed a NEW voucher from an existing one. Do NOT set editIdRef
      // — that guarantees `handleConfirmPost` calls `create()`, not `update()`.
      // Voucher-no is left blank (auto or user-entered on save); date defaults to today.
      editIdRef.current = null;
      rowVersionRef.current = 0;
      journalEntryService.getById(transactionRef.current.id).then(tx => {
        if (cancelToken.cancelled) return;
        const f: JournalForm = {
          voucherTypeId: tx.voucherTypeId,
          date: defaultDateIn(fyRef.current),
          reference: tx.reference || '',
          narration: tx.narration || '',
          manualNumber: '',
        };
        const mapped: LineRow[] = (tx.lines || []).map(ln => ({
          key: ++lineKey.current,
          ledgerId: ln.ledgerId,
          ledgerLabel: ln.ledgerCode ? `${ln.ledgerCode} - ${ln.ledgerName}` : (ln.ledgerName || ''),
          ledgerIsOpenItem: ledgerOpenItemCache.current.get(ln.ledgerId) ?? false,
          narration: ln.narration || '',
          debit: ln.drAmount ? String(ln.drAmount) : '',
          credit: ln.crAmount ? String(ln.crAmount) : '',
          allocations: [],
        }));
        const l = mapped.length >= 2 ? mapped : [...mapped, blankLine(), blankLine()];
        setForm(f);
        setLines(l);
        initialSnapshotRef.current = snapshot(f, l);
      }).catch((err: any) => {
        if (cancelToken.cancelled) return;
        notificationService.toast.error(err?.response?.data?.message || 'Could not load the source voucher.');
      });
      return () => { cancelToken.cancelled = true; };
    } else {
      editIdRef.current = null;
      const f = initialForm();
      const l = [blankLine(), blankLine()];
      setForm(f);
      setLines(l);
      initialSnapshotRef.current = snapshot(f, l);
    }
    return () => { cancelToken.cancelled = true; };
  }, [isOpen]);

  // Lookups (loaded every open so cross-tab voucher-type/ledger changes show).
  // Cancel-token pattern: each open captures a fresh { cancelled } ref that the
  // cleanup flips to true. Any late-arriving success OR failure from a prior
  // open is silently dropped instead of touching state / firing a toast on a
  // modal that's already closed (which caused "Could not load journal setup"
  // to appear right after a successful save).
  useEffect(() => {
    if (!isOpen) return;
    const cancelToken = { cancelled: false };
    (async () => {
      try {
        const [vt, cfg] = await Promise.all([
          voucherTypeService.getAll({ pageSize: 100 }),
          entityAccountingConfigService.getConfig().catch(() => null),
        ]);
        if (cancelToken.cancelled) return;
        setVoucherTypes(vt.data.filter(v => JOURNAL_CLASSES.includes(v.voucherClass) && v.statusCode !== 'INACTIVE'));
        if (cfg?.functionalCurrencyCode) setFunctionalCurrency(cfg.functionalCurrencyCode);
      } catch (err: any) {
        if (cancelToken.cancelled) return;
        notificationService.toast.error(err?.response?.data?.message || 'Could not load journal setup.');
      }
    })();
    return () => { cancelToken.cancelled = true; };
  }, [isOpen]);

  const selectedVoucherType = useMemo(() => voucherTypes.find(v => v.id === form.voucherTypeId), [voucherTypes, form.voucherTypeId]);
  const isManual = selectedVoucherType?.numberingMethod === 'MANUAL';
  const allowOverride = selectedVoucherType?.allowManualEntry ?? false;
  // In edit mode the document number + voucher type are fixed (identity preserved).
  const canEditNumber = !isEdit && (isManual || allowOverride);

  const voucherTypeOptions: SelectOption[] = useMemo(
    () => voucherTypes.map(v => ({ value: v.id, label: `${v.voucherCode} - ${v.voucherName}` })), [voucherTypes]);

  const loadLedgerOptions = useCallback(async (input: string): Promise<SelectOption[]> => {
    // ADR-0027 ③c: transaction pickers only offer ledgers available to the session BU.
    const res = await coaLedgerService.getAll({
      pageSize: 50, searchTerm: input || undefined,
      availableToBusinessUnitId: sessionBuId,
    });
    res.data.forEach(l => {
      ledgerLabelCache.current.set(l.id, `${l.ledgerCode} - ${l.ledgerName}`);
      ledgerOpenItemCache.current.set(l.id, !!l.isOpenItemManaged);
    });
    return res.data.map(l => ({ value: l.id, label: `${l.ledgerCode} - ${l.ledgerName}` }));
  }, [sessionBuId]);

  // Lines loaded from a saved voucher (edit / copy) name ledgers the picker never listed, so their
  // open-item flag is unknown: read those ledgers once, then show the bill-wise panel where it applies.
  const unresolvedLedgerKey = useMemo(() => Array.from(new Set(
    lines.filter(l => l.ledgerId && !ledgerOpenItemCache.current.has(l.ledgerId)).map(l => l.ledgerId))).sort().join(','), [lines]);
  useEffect(() => {
    if (!unresolvedLedgerKey) return;
    let active = true;
    Promise.all(unresolvedLedgerKey.split(',').map(id => coaLedgerService.getById(id)
      .then(r => { ledgerOpenItemCache.current.set(id, !!r?.data?.isOpenItemManaged); })
      .catch(() => { ledgerOpenItemCache.current.set(id, false); })))
      .then(() => {
        if (!active) return;
        setLines(prev => prev.map(l => (l.ledgerId && !l.ledgerIsOpenItem && ledgerOpenItemCache.current.get(l.ledgerId) ? { ...l, ledgerIsOpenItem: true } : l)));
      });
    return () => { active = false; };
  }, [unresolvedLedgerKey]);

  const loadOutstanding = useCallback(async (ledgerId: string, side: string, input: string): Promise<SelectOption[]> => {
    if (!ledgerId) return [];
    const items = await journalEntryService.getOutstandingItems(ledgerId, undefined, side);
    const opts = items.map(it => ({ value: it.id, label: `${it.referenceNo} · ${it.drCr} ${it.fcBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }));
    opts.forEach(o => billLabelCache.current.set(o.value, o.label));
    return input ? opts.filter(o => o.label.toLowerCase().includes(input.toLowerCase())) : opts;
  }, []);

  // Fetch the scoped ledger balance as of the voucher date (BU-wise / consolidated per context).
  const fetchLedgerBalance = useCallback(async (ledgerId: string) => {
    if (!ledgerId) return;
    try {
      // In edit mode exclude this voucher so its own amount isn't double-counted (handles date changes too).
      const exclude = modeRef.current === 'edit' ? (editIdRef.current ?? undefined) : undefined;
      const b = await journalEntryService.getLedgerBalance(ledgerId, formRef.current.date || undefined, exclude);
      setLedgerBalances(prev => ({ ...prev, [ledgerId]: b.balance }));
    } catch { /* balance is advisory; ignore */ }
  }, []);

  // Voucher date drives the as-of balance — clear + refetch for ledgers already on the form.
  useEffect(() => {
    if (!isOpen) return;
    setLedgerBalances({});
    Array.from(new Set(linesRef.current.filter(l => l.ledgerId).map(l => l.ledgerId))).forEach(fetchLedgerBalance);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.date, isOpen]);

  const totalDebit = useMemo(() => lines.reduce((s, l) => s + num(l.debit), 0), [lines]);
  const totalCredit = useMemo(() => lines.reduce((s, l) => s + num(l.credit), 0), [lines]);
  const difference = totalDebit - totalCredit;
  const balanced = Math.abs(difference) < 0.005 && totalDebit > 0;

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return snapshot(form, lines) !== initialSnapshotRef.current;
  }, [form, lines, snapshot]);

  const updateForm = useCallback((patch: Partial<JournalForm>) => setForm(prev => ({ ...prev, ...patch })), []);
  const updateLine = useCallback((key: number, patch: Partial<LineRow>) => setLines(prev => prev.map(l => (l.key === key ? { ...l, ...patch } : l))), []);
  const setDebit = useCallback((key: number, v: string) => updateLine(key, { debit: v, credit: '' }), [updateLine]);
  const setCredit = useCallback((key: number, v: string) => updateLine(key, { credit: v, debit: '' }), [updateLine]);
  const addRow = useCallback(() => setLines(prev => [...prev, blankLine()]), [blankLine]);
  const removeRow = useCallback((key: number) => setLines(prev => (prev.length > 1 ? prev.filter(l => l.key !== key) : prev)), []);

  // Bill-wise allocations on an open-item line.
  const addAlloc = useCallback((lineKey: number) => setLines(prev => prev.map(l => (l.key === lineKey ? { ...l, allocations: [...l.allocations, blankAlloc()] } : l))), [blankAlloc]);
  const updateAlloc = useCallback((lineKey: number, ak: number, patch: Partial<AllocRow>) => setLines(prev => prev.map(l => (l.key === lineKey ? { ...l, allocations: l.allocations.map(a => (a.key === ak ? { ...a, ...patch } : a)) } : l))), []);
  const removeAlloc = useCallback((lineKey: number, ak: number) => setLines(prev => prev.map(l => (l.key === lineKey ? { ...l, allocations: l.allocations.filter(a => a.key !== ak) } : l))), []);

  const handleClose = useCallback(() => { if (isDirty) setDiscardConfirmOpen(true); else onClose(); }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const f = formRef.current;
    const l = linesRef.current;
    const currentMode = modeRef.current;
    const editing = currentMode === 'edit';
    const copying = currentMode === 'copy';
    const cId = createInstanceIdRef.current || '1';
    const cpId = copyInstanceIdRef.current || '1';
    const sourceTxNo = transactionRef.current?.transactionNo;
    const savedSnapshot = initialSnapshotRef.current;
    const savedEditId = editIdRef.current;
    const savedRowVersion = rowVersionRef.current;
    const currentIsDirty = savedSnapshot ? snapshot(f, l) !== savedSnapshot : false;
    const modalId = editing
      ? `${tabId}-form-modal-edit-${f.manualNumber}`
      : copying
      ? `${tabId}-form-modal-copy-${cpId}`
      : `${tabId}-form-modal-create-${cId}`;
    const title = editing
      ? `Edit JE - ${f.manualNumber || 'voucher'}`
      : copying
      ? `Copy JE - ${sourceTxNo || 'voucher'} (${cpId})`
      : `Journal Entry (${cId})`;
    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setForm(f);
        setLines(l);
        initialSnapshotRef.current = savedSnapshot;
        if (editing) { editIdRef.current = savedEditId; rowVersionRef.current = savedRowVersion; }
        onRestoreModal?.(
          editing ? 'edit' : copying ? 'copy' : 'create',
          editing ? undefined : copying ? cpId : cId,
          editing ? f.manualNumber : copying ? sourceTxNo : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal, snapshot]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const validate = useCallback((): string | null => {
    if (!form.voucherTypeId) return 'Select a voucher type.';
    if (isManual && !form.manualNumber.trim()) return 'Enter the document number (manual numbering).';
    const filled = lines.filter(l => l.ledgerId && (num(l.debit) > 0 || num(l.credit) > 0));
    if (filled.length < 2) return 'A journal needs at least two lines with a ledger and an amount.';
    if (lines.some(l => l.ledgerId && num(l.debit) === 0 && num(l.credit) === 0)) return 'Every ledger line needs a debit or a credit amount.';
    if (lines.some(l => !l.ledgerId && (num(l.debit) > 0 || num(l.credit) > 0))) return 'Every amount line needs a ledger.';
    if (totalDebit <= 0) return 'Voucher total cannot be zero.';
    if (!balanced) return `Voucher is not balanced — difference ${money(Math.abs(difference))}.`;
    // Bill-wise allocations (when entered) must total the line amount.
    for (const l of lines) {
      if (!l.ledgerId || !l.ledgerIsOpenItem) continue;
      const lineAmt = num(l.debit) + num(l.credit);
      const allocs = l.allocations.filter(a => num(a.amount) > 0);
      if (lineAmt <= 0 || allocs.length === 0) continue;
      if (allocs.some(a => a.mode === 'AGAINST_REF' && !a.openItemId)) return 'Pick a bill to settle for each Against-Ref allocation.';
      const allocSum = allocs.reduce((s, a) => s + num(a.amount), 0);
      if (Math.abs(allocSum - lineAmt) > 0.005) return `Bill allocations for "${l.ledgerLabel || 'a line'}" must total ${money(lineAmt)} (now ${money(allocSum)}).`;
    }
    return null;
  }, [form, isManual, lines, totalDebit, balanced, difference]);

  const handlePostClick = useCallback(() => {
    const err = validate();
    if (err) { notificationService.toast.warning(err); return; }
    setSaveConfirmOpen(true);
  }, [validate]);

  const handleConfirmPost = useCallback(async () => {
    setSaveConfirmOpen(false);
    setPosting(true);
    try {
      const payload: CreateJournalEntryRequest = {
        voucherTypeId: form.voucherTypeId,
        transactionDate: form.date,
        reference: form.reference.trim() || undefined,
        narration: form.narration.trim() || undefined,
        currencyCode: functionalCurrency,
        manualNumber: canEditNumber && form.manualNumber.trim() ? form.manualNumber.trim() : undefined,
        lines: lines
          .filter(l => l.ledgerId && (num(l.debit) > 0 || num(l.credit) > 0))
          .map(l => ({
            ledgerId: l.ledgerId,
            drAmount: num(l.debit),
            crAmount: num(l.credit),
            narration: l.narration.trim() || undefined,
            allocations: (l.ledgerIsOpenItem && l.allocations.some(a => num(a.amount) > 0))
              ? l.allocations.filter(a => num(a.amount) > 0).map(a => ({
                  mode: a.mode,
                  referenceNo: a.mode === 'AGAINST_REF' ? undefined : (a.referenceNo.trim() || undefined),
                  openItemId: a.mode === 'AGAINST_REF' ? (a.openItemId || undefined) : undefined,
                  dueDate: a.mode === 'NEW_REF' && a.dueDate ? a.dueDate : undefined,
                  amount: num(a.amount),
                }))
              // No bill-wise rows: the whole line is kept as the user chose (On Account by default).
              : l.ledgerIsOpenItem
                ? [{ mode: l.keepAs || 'ON_ACCOUNT', amount: num(l.debit) + num(l.credit) }]
                : undefined,
          })),
      };
      const tx = isEdit && editIdRef.current
        ? await journalEntryService.update(editIdRef.current, { ...payload, rowVersion: rowVersionRef.current })
        : await journalEntryService.create(payload);
      // mark clean so close doesn't prompt
      initialSnapshotRef.current = snapshot(form, lines);
      notificationService.toast.success(`Journal ${tx.transactionNo} ${isEdit ? 'updated' : 'posted'} successfully.`);
      onPosted();
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      // 409 = another user already saved this voucher first → prompt to refresh.
      if (status === 409) setConflict({ open: true, message: message || 'This voucher was modified by another user.' });
      else notificationService.toast.error(message || 'Could not post the journal entry.');
    } finally {
      setPosting(false);
    }
  }, [form, functionalCurrency, isManual, isEdit, lines, onPosted, onClose, snapshot]);

  // Reload the latest saved version into the form after a concurrency conflict.
  const handleRefreshFromConflict = useCallback(async () => {
    if (!editIdRef.current) { setConflict({ open: false, message: '' }); return; }
    setRefreshing(true);
    try {
      const tx = await journalEntryService.getById(editIdRef.current);
      const f: JournalForm = {
        voucherTypeId: tx.voucherTypeId,
        date: (tx.transactionDate || '').slice(0, 10) || today(),
        reference: tx.reference || '',
        narration: tx.narration || '',
        manualNumber: tx.transactionNo,
      };
      const mapped: LineRow[] = (tx.lines || []).map(ln => ({
        key: ++lineKey.current,
        ledgerId: ln.ledgerId,
        ledgerLabel: ln.ledgerCode ? `${ln.ledgerCode} - ${ln.ledgerName}` : (ln.ledgerName || ''),
        narration: ln.narration || '',
        debit: ln.drAmount ? String(ln.drAmount) : '',
        credit: ln.crAmount ? String(ln.crAmount) : '',
        ledgerIsOpenItem: ledgerOpenItemCache.current.get(ln.ledgerId) ?? false,
        allocations: [],
      }));
      const l = mapped.length >= 2 ? mapped : [...mapped, blankLine(), blankLine()];
      rowVersionRef.current = tx.rowVersion;
      setForm(f);
      setLines(l);
      initialSnapshotRef.current = snapshot(f, l);
      setConflict({ open: false, message: '' });
      notificationService.toast.info('Reloaded the latest version. Review the changes and save again.');
    } catch {
      notificationService.toast.error('Could not refresh. Please close and reopen the voucher.');
    } finally {
      setRefreshing(false);
    }
  }, [snapshot, blankLine]);

  if (!isOpen) return null;

  const labelStyle: React.CSSProperties = { fontSize: '0.8125rem', marginBottom: '0.25rem', display: 'block', color: 'var(--text-secondary)', fontWeight: 500 };
  const cellInput: React.CSSProperties = { width: '100%', textAlign: 'right', fontSize: '0.8125rem', padding: '0.375rem 0.5rem' };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        {posting && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', border: '3px solid var(--border-color)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Posting…</span>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{isEdit ? 'Edit Journal Entry' : isCopy ? `Copy Journal Entry${transaction ? ` — from ${transaction.transactionNo}` : ''}` : 'Journal Entry'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={posting}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={posting}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          {/* Header fields */}
          <div className="form-grid-4" style={{ alignItems: 'flex-end' }}>
            <div>
              <SyutSelect label={<>Voucher Type <span className="text-danger">*</span></>} options={voucherTypeOptions} menuPortalTarget={document.body}
                value={form.voucherTypeId} onChange={(v) => updateForm({ voucherTypeId: (v as string) || '' })} placeholder="Select journal type" isClearable={false} size="sm" isDisabled={isEdit} />
            </div>
            <div>
              <label style={labelStyle}>Date <span className="text-danger">*</span></label>
              <FyDateInput className="form-input" value={form.date} onChange={v => updateForm({ date: v })} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <DocumentNumberField compact label="Voucher No" voucherTypeId={form.voucherTypeId} date={form.date}
              existingNumber={isEdit ? form.manualNumber : ''} manualValue={form.manualNumber}
              onManualChange={(v) => updateForm({ manualNumber: v })} required />
            <div>
              <label style={labelStyle}>Reference</label>
              <input type="text" className="form-input" value={form.reference} onChange={(e) => updateForm({ reference: e.target.value })} placeholder="e.g. adjustment ref" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
          </div>
          <div style={{ marginTop: '0.75rem' }}>
            <label style={labelStyle}>Narration</label>
            <input type="text" className="form-input" value={form.narration} onChange={(e) => updateForm({ narration: e.target.value })} placeholder="Voucher narration" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
          </div>

          {/* Lines */}
          <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem', marginTop: '1rem' }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <th style={{ width: 32, textAlign: 'center', padding: '0.5rem' }}>#</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Ledger</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Narration</th>
                <th style={{ width: 140, textAlign: 'right', padding: '0.5rem' }}>Debit</th>
                <th style={{ width: 140, textAlign: 'right', padding: '0.5rem' }}>Credit</th>
                <th style={{ width: 36, padding: '0.5rem' }} />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, idx) => (
                <React.Fragment key={l.key}>
                <tr style={{ borderBottom: l.ledgerIsOpenItem ? 'none' : '1px solid var(--border-color)' }}>
                  <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                  <td style={{ padding: '0.25rem 0.5rem', minWidth: 260 }}>
                    <SyutSelect isAsync loadOptions={loadLedgerOptions} defaultOptions cacheOptions={false}
                      seedOptions={l.ledgerId ? [{ value: l.ledgerId, label: l.ledgerLabel }] : undefined} menuPortalTarget={document.body}
                      value={l.ledgerId}
                      onChange={(v) => { updateLine(l.key, { ledgerId: (v as string) || '', ledgerLabel: (v && ledgerLabelCache.current.get(v as string)) || '', ledgerIsOpenItem: !!(v && ledgerOpenItemCache.current.get(v as string)), allocations: [] }); if (v) fetchLedgerBalance(v as string); }}
                      placeholder="Pick a ledger / party" isClearable size="sm" />
                    {l.ledgerId && ledgerBalances[l.ledgerId] !== undefined && (() => {
                      const asOf = ledgerBalances[l.ledgerId];
                      const projected = asOf + (num(l.debit) - num(l.credit));
                      const fmtBal = (n: number) => `${money(Math.abs(n))} ${n >= 0 ? 'Dr' : 'Cr'}`;
                      return (
                        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                          Bal {fmtBal(asOf)}{(num(l.debit) > 0 || num(l.credit) > 0) ? <> · after <strong>{fmtBal(projected)}</strong></> : ''}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: '0.25rem 0.5rem' }}>
                    <input type="text" className="form-input" value={l.narration} onChange={(e) => updateLine(l.key, { narration: e.target.value })} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.5rem' }} />
                  </td>
                  <td style={{ padding: '0.25rem 0.5rem' }}>
                    <input type="number" inputMode="decimal" className="form-input" value={l.debit} onChange={(e) => setDebit(l.key, e.target.value)} placeholder="0.00" style={cellInput} />
                  </td>
                  <td style={{ padding: '0.25rem 0.5rem' }}>
                    <input type="number" inputMode="decimal" className="form-input" value={l.credit} onChange={(e) => setCredit(l.key, e.target.value)} placeholder="0.00" style={cellInput} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button title="Remove line" onClick={() => removeRow(l.key)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.25rem' }}>
                      <IconSystem.trash size={15} />
                    </button>
                  </td>
                </tr>
                {l.ledgerIsOpenItem && (() => {
                  const lineAmt = num(l.debit) + num(l.credit);
                  const allocSum = l.allocations.reduce((s, a) => s + num(a.amount), 0);
                  const lineSide = num(l.debit) > 0 ? 'DR' : 'CR';
                  const pickerSide = lineSide === 'DR' ? 'CR' : 'DR';
                  return (
                    <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td />
                      <td colSpan={5} style={{ padding: '0 0.5rem 0.75rem' }}>
                        <div style={{ background: 'var(--bg-secondary)', borderRadius: '0.5rem', padding: '0.5rem 0.75rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)' }}>Bill-wise allocation</span>
                            {l.allocations.length > 0 && (
                              <span style={{ fontSize: '0.72rem', color: Math.abs(allocSum - lineAmt) > 0.005 ? '#dc2626' : 'var(--text-muted)' }}>
                                Allocated {money(allocSum)} of {money(lineAmt)}
                              </span>
                            )}
                          </div>
                          {l.allocations.length === 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.4rem', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                              <span>Not allocated to bills — keep {money(lineAmt)} as</span>
                              <div style={{ width: 190 }}>
                                <SyutSelect options={KEEP_AS_OPTIONS} value={l.keepAs || 'ON_ACCOUNT'} menuPortalTarget={document.body}
                                  onChange={(v) => updateLine(l.key, { keepAs: (v as string) || 'ON_ACCOUNT' })} isClearable={false} size="sm" />
                              </div>
                              <span style={{ color: 'var(--text-muted)' }}>
                                {(l.keepAs || 'ON_ACCOUNT') === 'NEW_REF'
                                  ? 'a new bill on the party’s ledger'
                                  : 'unallocated on the party’s ledger — match it later with a set-off or an Against Ref'}
                              </span>
                            </div>
                          )}
                          {l.allocations.map(a => (
                            <div key={a.key} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', marginBottom: '0.35rem', flexWrap: 'wrap' }}>
                              <div style={{ width: 180 }}>
                                <SyutSelect options={ALLOC_MODE_OPTIONS} value={a.mode} menuPortalTarget={document.body}
                                  onChange={(v) => updateAlloc(l.key, a.key, { mode: (v as string) || 'NEW_REF', openItemId: '', openItemLabel: '' })} isClearable={false} size="sm" />
                              </div>
                              {a.mode === 'AGAINST_REF' ? (
                                <div style={{ flex: 1, minWidth: 200 }}>
                                  <SyutSelect isAsync loadOptions={(inp) => loadOutstanding(l.ledgerId, pickerSide, inp)} defaultOptions cacheOptions={false}
                                    seedOptions={a.openItemId ? [{ value: a.openItemId, label: a.openItemLabel }] : undefined} menuPortalTarget={document.body}
                                    value={a.openItemId} onChange={(v) => updateAlloc(l.key, a.key, { openItemId: (v as string) || '', openItemLabel: (v && billLabelCache.current.get(v as string)) || '' })}
                                    placeholder="Pick a bill to settle" isClearable size="sm" />
                                </div>
                              ) : (
                                <input type="text" className="form-input" value={a.referenceNo} onChange={(e) => updateAlloc(l.key, a.key, { referenceNo: e.target.value })} placeholder="Bill / ref no (optional)" style={{ flex: 1, minWidth: 160, fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} />
                              )}
                              {a.mode === 'NEW_REF' && (
                                <input type="date" className="form-input" value={a.dueDate} onChange={(e) => updateAlloc(l.key, a.key, { dueDate: e.target.value })} title="Due date" style={{ width: 140, fontSize: '0.78rem', padding: '0.3rem 0.4rem' }} />
                              )}
                              <input type="number" inputMode="decimal" className="form-input" value={a.amount} onChange={(e) => updateAlloc(l.key, a.key, { amount: e.target.value })} placeholder="Amount" style={{ width: 110, textAlign: 'right', fontSize: '0.78rem', padding: '0.3rem 0.5rem' }} />
                              <button title="Remove allocation" onClick={() => removeAlloc(l.key, a.key)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '0.2rem' }}><IconSystem.x size={14} /></button>
                            </div>
                          ))}
                          <button className="btn btn-secondary" onClick={() => addAlloc(l.key)} style={{ fontSize: '0.75rem', padding: '0.25rem 0.6rem' }}><IconSystem.add size={12} /> Allocation</button>
                        </div>
                      </td>
                    </tr>
                  );
                })()}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--bg-secondary)' }}>
                <td colSpan={3} style={{ textAlign: 'right', padding: '0.5rem' }}>Total</td>
                <td style={{ textAlign: 'right', padding: '0.5rem' }}>{money(totalDebit)}</td>
                <td style={{ textAlign: 'right', padding: '0.5rem' }}>{money(totalCredit)}</td>
                <td />
              </tr>
            </tfoot>
          </table>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.75rem', flexWrap: 'wrap', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={addRow} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.75rem' }}>
              <IconSystem.add size={14} /> Add Row
            </button>
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Currency <strong>{functionalCurrency}</strong> &nbsp;·&nbsp; Difference:&nbsp;
              <strong style={{ color: balanced ? '#16a34a' : '#dc2626' }}>{money(Math.abs(difference))}</strong>
              &nbsp;{balanced ? '✓ balanced' : difference > 0 ? '(Dr heavy)' : '(Cr heavy)'}
            </span>
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={posting}>Cancel</button>
          <button className="btn btn-primary" onClick={handlePostClick} disabled={!balanced || posting}>
            <IconSystem.check size={16} /> {isEdit ? 'Save Changes' : 'Post Journal'}
          </button>
        </div>
      </div>

      <ConfirmDialog open={saveConfirmOpen}
        title={isEdit ? 'Save Changes' : isCopy ? 'Post New Journal Entry' : 'Post Journal Entry'}
        message={isEdit
          ? `Save changes to voucher ${form.manualNumber} (${functionalCurrency} ${money(totalDebit)})?`
          : isCopy
          ? `Post this as a new voucher for ${functionalCurrency} ${money(totalDebit)}? The original voucher ${transaction?.transactionNo || ''} is not affected.`
          : `Post this balanced voucher of ${functionalCurrency} ${money(totalDebit)}?`}
        confirmLabel={isEdit ? 'Save' : 'Post'} cancelLabel="Cancel" variant="default"
        onConfirm={handleConfirmPost} onCancel={() => setSaveConfirmOpen(false)} />

      <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />

      <ConfirmDialog open={conflict.open} title="Voucher Modified by Another User"
        message={conflict.message || 'This voucher was modified by another user while you were editing. Refresh to load their latest version, then review and save again.'}
        confirmLabel={refreshing ? 'Refreshing…' : 'Refresh from Server'} cancelLabel="Keep My Changes" variant="default"
        loading={refreshing} loadingMessage="Refreshing…"
        onConfirm={handleRefreshFromConflict} onCancel={() => setConflict({ open: false, message: '' })} />
    </>
  );
};

// ============================================================================
// VIEW MODAL — read-only posted voucher (header + lines).
// ============================================================================

export interface JournalEntryViewModalProps {
  isOpen: boolean;
  transaction: TransactionDto | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (transactionNo: string) => void;
}

export const JournalEntryViewModal: React.FC<JournalEntryViewModalProps> = ({
  isOpen, transaction, onClose, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const [detail, setDetail] = useState<TransactionDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !transaction?.id) { setDetail(null); return; }
    setLoading(true);
    journalEntryService.getById(transaction.id)
      .then(setDetail)
      .catch(() => setDetail(transaction))
      .finally(() => setLoading(false));
  }, [isOpen, transaction]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !transaction) return;
    minimize({
      id: `${tabId}-view-modal-${transaction.transactionNo}`,
      title: `View - ${transaction.transactionNo}`,
      moduleName, tabId,
      onRestore: () => onRestoreView?.(transaction.transactionNo),
    });
    onClose();
  }, [tabId, moduleName, transaction, minimize, onClose, onRestoreView]);

  if (!isOpen || !transaction) return null;
  const t = detail ?? transaction;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-xl">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Voucher — {t.transactionNo}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar"><IconSystem.chevronDown size={16} /></button>
            )}
            <button className="modal-close" onClick={onClose}><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.8125rem' }}>
            <div><div style={{ color: 'var(--text-muted)' }}>Voucher No</div><strong>{t.transactionNo}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Date</div><strong>{t.transactionDate ? new Date(t.transactionDate).toLocaleDateString() : '—'}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Type</div><strong>{t.voucherTypeName || t.voucherClass}</strong></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Status</div><span className="badge">{t.status}</span></div>
            <div><div style={{ color: 'var(--text-muted)' }}>Reference</div><strong>{t.reference || '—'}</strong></div>
            <div style={{ gridColumn: 'span 3' }}><div style={{ color: 'var(--text-muted)' }}>Narration</div><strong>{t.narration || '—'}</strong></div>
          </div>

          <table className="syut-grid" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg, #dbeafe)' }}>
                <th style={{ width: 32, textAlign: 'center', padding: '0.5rem' }}>#</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Ledger</th>
                <th style={{ textAlign: 'left', padding: '0.5rem' }}>Narration</th>
                <th style={{ width: 140, textAlign: 'right', padding: '0.5rem' }}>Debit</th>
                <th style={{ width: 140, textAlign: 'right', padding: '0.5rem' }}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>Loading…</td></tr>}
              {!loading && (t.lines || []).map((l, idx) => (
                <tr key={l.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ textAlign: 'center', color: 'var(--text-muted)' }}>{idx + 1}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{l.ledgerCode ? `${l.ledgerCode} - ${l.ledgerName}` : l.ledgerName}</td>
                  <td style={{ padding: '0.375rem 0.5rem' }}>{l.narration || '—'}</td>
                  <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right' }}>{l.drAmount ? money(l.drAmount) : ''}</td>
                  <td style={{ padding: '0.375rem 0.5rem', textAlign: 'right' }}>{l.crAmount ? money(l.crAmount) : ''}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ fontWeight: 700, background: 'var(--bg-secondary)' }}>
                <td colSpan={3} style={{ textAlign: 'right', padding: '0.5rem' }}>Total ({t.currencyCode})</td>
                <td style={{ textAlign: 'right', padding: '0.5rem' }}>{money(t.totalBaseAmount)}</td>
                <td style={{ textAlign: 'right', padding: '0.5rem' }}>{money(t.totalBaseAmount)}</td>
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Posted{t.createdByName ? ` by ${t.createdByName}` : ''}{t.createdAt ? ` on ${new Date(t.createdAt).toLocaleString()}` : ''}.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default JournalEntryFormModal;
